type InvokeErrorBody = Record<string, unknown>;

function parseJsonBody(body: unknown): InvokeErrorBody | null {
  if (!body) return null;
  if (typeof body === "object") return body as InvokeErrorBody;
  if (typeof body !== "string") return null;
  try {
    const parsed = JSON.parse(body);
    return parsed && typeof parsed === "object" ? (parsed as InvokeErrorBody) : null;
  } catch {
    return null;
  }
}

/** Extrai o JSON do corpo de erro retornado por `supabase.functions.invoke`. */
export function parseInvokeErrorBody(error: unknown): InvokeErrorBody | null {
  if (!(error instanceof Error)) return null;
  const ctx = (error as { context?: { body?: unknown } }).context;
  return parseJsonBody(ctx?.body);
}

export function extractGraphErrorMessage(graphError: unknown): string | null {
  if (!graphError) return null;
  if (typeof graphError === "string") return graphError;
  if (typeof graphError !== "object") return null;

  const err = graphError as {
    message?: unknown;
    error_user_msg?: unknown;
    error_user_title?: unknown;
  };

  if (typeof err.message === "string" && err.message.trim()) return err.message;
  if (typeof err.error_user_msg === "string" && err.error_user_msg.trim()) {
    return err.error_user_msg;
  }
  if (typeof err.error_user_title === "string" && err.error_user_title.trim()) {
    return err.error_user_title;
  }

  try {
    const serialized = JSON.stringify(graphError);
    return serialized === "{}" ? null : serialized;
  } catch {
    return null;
  }
}

export function getInvokeErrorText(error: unknown, data?: unknown): string {
  const payload =
    (data && typeof data === "object" ? (data as InvokeErrorBody) : null) ?? parseInvokeErrorBody(error);

  if (payload) {
    const graphMsg = extractGraphErrorMessage(payload.graph_error);
    if (graphMsg) return graphMsg;

    if (typeof payload.message === "string" && payload.message.trim()) return payload.message;
    if (typeof payload.error === "string" && payload.error.trim() && payload.error !== "Graph error") {
      return payload.error;
    }
  }

  if (!error) return "";

  if (typeof error === "string") return error;

  // Supabase Functions errors usually extend Error and include a `context` field
  if (error instanceof Error) {
    const anyErr = error as { context?: { status?: number } };
    const status = typeof anyErr?.context?.status === "number" ? `status:${anyErr.context.status}` : "";
    return [error.message, status].filter(Boolean).join(" | ");
  }

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

/**
 * Versão assíncrona: `supabase.functions.invoke` entrega o corpo do erro como
 * `Response` (stream não lido). Sem ler o stream, perdemos a mensagem real do
 * servidor e o usuário vê apenas "non-2xx status code | status:400".
 */
export async function readInvokeErrorText(error: unknown, data?: unknown): Promise<string> {
  const ctx = error instanceof Error ? (error as { context?: unknown }).context : null;
  const response = ctx as Response | undefined;

  if (response && typeof (response as Response).clone === "function") {
    try {
      const text = await (response as Response).clone().text();
      const parsed = parseJsonBody(text);
      if (parsed) {
        const direct = getInvokeErrorText(error, parsed);
        if (direct) return direct;
      } else if (text.trim()) {
        return text.trim();
      }
    } catch {
      /* segue para o fallback síncrono */
    }
  }

  return getInvokeErrorText(error, data);
}

export function isGoogleCalendarReconnectError(errorText: string): boolean {
  const t = (errorText || "").toLowerCase();
  return t.includes("google_auth_needs_reconnect");
}
